import type {
  FormInsuranceCheckRequest,
  InsuranceCheckOutcome,
  InsuranceUnavailableReason,
  IntakeFormField,
  PolicySlot,
} from '../../api/bookingApi';
import { isFieldVisible } from './fieldVisibility';
import type { V2Item } from './types';

// The live insurance check on v2 forms (booking-frontend#46, backend#3197), as
// pure functions: which slots a package can check, the request a slot's answers
// build, the per-slot attempt gate, and what each outcome shows. The renderer
// owns the I/O and the screen state; nothing here reads or writes storage.

export const POLICY_SLOTS: readonly PolicySlot[] = ['primary', 'secondary'];

/** Max checks the server runs per package instance and slot; a third is never sent. */
export const MAX_CHECKS_PER_SLOT = 2;

/** How long Next waits for a check before advancing silently. */
export const INSURANCE_CHECK_WAIT_MS = 10000;

/** The shipped templates' per-slot keys (backend `form_insurance.py`). "Local #" is
 *  the member/subscriber ID. */
export function slotFieldKeys(slot: PolicySlot) {
  return {
    company: `insurance_company_${slot}`,
    memberId: `local_number_${slot}`,
    policyholder: `policyholder_${slot}`,
    insuredName: `insured_name_${slot}`,
    insuredDob: `insured_dob_${slot}`,
    relationship: `policyholder_relationship_${slot}`,
  };
}

/** The template fields that hold the patient's own name and DOB. */
const PATIENT_MAPPINGS = ['first_name', 'last_name', 'date_of_birth'] as const;
type PatientMapping = typeof PATIENT_MAPPINGS[number];

// The backend's `FormInsuranceCheckRequest` limits; a longer value would be a 422.
const MAX_ID_LENGTH = 80;
const MAX_NAME_LENGTH = 100;

// ---------------------------------------------------------------------------
// Names and relationships
// ---------------------------------------------------------------------------

// Mirrors `_SURNAME_PARTICLES` / `_NAME_SUFFIXES` in the backend's ehr_insurance.py,
// entries that can never match a single token included, so the two stay diffable.
const SURNAME_PARTICLES = new Set([
  'de', 'del', 'de la', 'della', 'di', 'da', 'dos', 'das', 'la', 'le', 'van',
  'von', 'der', 'den', 'ter', 'bin', 'ibn', 'al', 'st', 'st.', 'mac', 'mc', "o'",
]);
const NAME_SUFFIXES = new Set([
  'jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v', 'md', 'dds', 'dmd', 'phd', 'esq',
]);

function stripChars(value: string, chars: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && chars.indexOf(value[start]) !== -1) start += 1;
  while (end > start && chars.indexOf(value[end - 1]) !== -1) end -= 1;
  return value.slice(start, end);
}

/**
 * Split "Insured's Name" into [first, last] exactly as the backend's `_split_name`
 * does, so the check sends the names the insurer will be matched on: "Last, First"
 * is honoured, middle names and initials are dropped, trailing suffixes belong to
 * neither part, and a surname particle starts a multi-word surname.
 */
export function splitName(name: string | null | undefined): [string | null, string | null] {
  if (!name) return [null, null];
  const trimmed = name.trim();
  const comma = trimmed.indexOf(',');
  if (comma !== -1) {
    const last = trimmed.slice(0, comma).trim();
    const firstParts = trimmed.slice(comma + 1).split(/\s+/).filter(Boolean);
    return [firstParts[0] ?? null, last || null];
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  while (parts.length > 1 && NAME_SUFFIXES.has(stripChars(parts[parts.length - 1], '.,').toLowerCase())) {
    parts.pop();
  }
  if (parts.length === 0) return [null, null];
  if (parts.length === 1) return [parts[0], null];
  let start = parts.length - 1;
  for (let i = 1; i < parts.length - 1; i += 1) {
    if (SURNAME_PARTICLES.has(stripChars(parts[i], '.').toLowerCase())) {
      start = i;
      break;
    }
  }
  return [parts[0], parts.slice(start).join(' ')];
}

// "Relationship to the patient" names the policyholder from the patient's side;
// the check needs the patient's relationship to the policyholder. A parent or
// guardian holding the policy makes the patient their `child`; a spouse or
// partner makes them a `spouse`. "Son", "Daughter" and everything else is `other`.
const PARENT_WORDS = new Set([
  'mother', 'father', 'mom', 'mommy', 'mama', 'momma', 'dad', 'daddy', 'papa', 'mum', 'mummy',
  'parent', 'parents', 'guardian', 'guardians',
  'stepmother', 'stepfather', 'stepmom', 'stepmum', 'stepdad', 'stepparent', 'stepparents',
]);
const SPOUSE_WORDS = new Set(['wife', 'husband', 'spouse', 'partner']);
// Qualifiers that make a parent or spouse word name someone else: a mother-in-law,
// a grandmother written as two words, an ex-wife.
const DISQUALIFIERS = new Set(['law', 'grand', 'great', 'god', 'ex', 'former']);

export function mapRelationship(text: unknown): 'child' | 'spouse' | 'other' {
  if (typeof text !== 'string') return 'other';
  const words = text.toLowerCase().split(/[^a-z]+/).filter(Boolean);
  if (words.some((w) => DISQUALIFIERS.has(w))) return 'other';
  const parent = words.some((w) => PARENT_WORDS.has(w));
  const spouse = words.some((w) => SPOUSE_WORDS.has(w));
  if (parent && !spouse) return 'child';
  if (spouse && !parent) return 'spouse';
  return 'other';
}

// ---------------------------------------------------------------------------
// Building the request
// ---------------------------------------------------------------------------

/** One slot's raw answers, as the request builder sees them. */
export interface SlotAnswers {
  slot: PolicySlot;
  policyholder: unknown;
  payerId: unknown;
  memberId: unknown;
  insuredName: unknown;
  insuredDob: unknown;
  relationship: unknown;
  /** The patient's own answers, or null when the package has no field for them. */
  patient: { firstName: unknown; lastName: unknown; dob: unknown } | null;
}

export type PolicyholderChoice = 'self' | 'someone_else' | null;

export function policyholderChoice(answer: unknown): PolicyholderChoice {
  if (typeof answer !== 'string') return null;
  const normalized = answer.trim().toLowerCase();
  if (normalized === 'me (the patient)') return 'self';
  if (normalized === 'someone else') return 'someone_else';
  return null;
}

function clean(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed !== '' && trimmed.length <= max ? trimmed : null;
}

export function isIsoDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/**
 * The check request for one slot, or null when it must not be sent: the
 * policyholder question is unanswered (or answered with something else), or a
 * picked payer, the member ID, a first or last name, or a valid YYYY-MM-DD DOB
 * is missing. "Me" on a package that never asks the patient's name or DOB sends
 * no identity at all, and the backend uses the patient record (backend#3519).
 */
export function buildInsuranceCheckRequest(answers: SlotAnswers): FormInsuranceCheckRequest | null {
  const choice = policyholderChoice(answers.policyholder);
  if (!choice) return null;
  const payerId = clean(answers.payerId, MAX_ID_LENGTH);
  const memberId = clean(answers.memberId, MAX_ID_LENGTH);
  if (!payerId || !memberId) return null;

  let first: string | null;
  let last: string | null;
  let dob: unknown;
  if (choice === 'self') {
    if (!answers.patient) {
      return { policy_slot: answers.slot, relationship_to_policyholder: 'self', payer_id: payerId, member_id: memberId };
    }
    first = clean(answers.patient.firstName, MAX_NAME_LENGTH);
    last = clean(answers.patient.lastName, MAX_NAME_LENGTH);
    dob = typeof answers.patient.dob === 'string' ? answers.patient.dob.trim() : null;
  } else {
    const [f, l] = splitName(typeof answers.insuredName === 'string' ? answers.insuredName : null);
    first = clean(f, MAX_NAME_LENGTH);
    last = clean(l, MAX_NAME_LENGTH);
    dob = typeof answers.insuredDob === 'string' ? answers.insuredDob.trim() : null;
  }
  if (!first || !last || !isIsoDate(dob)) return null;

  return {
    policy_slot: answers.slot,
    relationship_to_policyholder: choice === 'self' ? 'self' : mapRelationship(answers.relationship),
    payer_id: payerId,
    member_id: memberId,
    policyholder_first_name: first,
    policyholder_last_name: last,
    policyholder_date_of_birth: dob,
  };
}

/** Stable identity of a request: the same answers always give the same string. */
export function requestFingerprint(request: FormInsuranceCheckRequest): string {
  return JSON.stringify([
    request.policy_slot,
    request.relationship_to_policyholder,
    request.payer_id,
    request.member_id,
    request.policyholder_first_name,
    request.policyholder_last_name,
    request.policyholder_date_of_birth,
  ]);
}

/** The normalized identifiers that can make a refused or failed check worth correcting. */
function policyFingerprint(request: FormInsuranceCheckRequest): string {
  return JSON.stringify([request.payer_id, request.member_id]);
}

// ---------------------------------------------------------------------------
// Slots in a package
// ---------------------------------------------------------------------------

export interface FieldRef {
  itemId: string;
  key: string;
}

/** A slot the package can check: one item holds its typeahead company field and
 *  its "Who is the policyholder?" select (decision 2: no question, no check). */
export interface InsuranceSlot {
  slot: PolicySlot;
  item: V2Item;
  companyField: IntakeFormField & { payer_id_key: string };
}

function itemFields(item: V2Item): IntakeFormField[] {
  const out: IntakeFormField[] = [];
  item.sections.forEach((section) => section.fields.forEach((field) => out.push(field)));
  return out;
}

export function findInsuranceSlots(items: V2Item[]): InsuranceSlot[] {
  const out: InsuranceSlot[] = [];
  POLICY_SLOTS.forEach((slot) => {
    const keys = slotFieldKeys(slot);
    for (const item of items) {
      if (item.kind === 'agreement') continue;
      const fields = itemFields(item);
      const company = fields.find((f) => f.key === keys.company && f.field_type === 'text' && !!f.payer_id_key);
      const question = fields.find((f) => f.key === keys.policyholder && f.field_type === 'select');
      if (company && question) {
        out.push({ slot, item, companyField: company as InsuranceSlot['companyField'] });
        return;
      }
    }
  });
  return out;
}

/** Everything the renderer needs to decide on, send and explain one slot's check. */
export interface SlotCheckPlan {
  slot: PolicySlot;
  request: FormInsuranceCheckRequest | null;
  fingerprint: string | null;
  policyholder: PolicyholderChoice;
  /** The company name as answered (the picked payer's display name). */
  companyName: string;
  /** Fields whose edits can change this slot's request: Next from a screen that
   *  holds any of them runs the check. */
  inputs: FieldRef[];
  memberIdField: FieldRef | null;
  /** The policyholder's name and DOB: the insured fields, or the patient's own. */
  nameAndDobFields: FieldRef[];
}

type FormValues = Record<string, Record<string, unknown>>;

/** The item's field `key` and its answer (undefined while the field is hidden),
 *  or null when the item has no such field. */
function slotAnswer(item: V2Item, key: string, formValues: FormValues): { field: IntakeFormField; value: unknown } | null {
  const field = itemFields(item).find((f) => f.key === key);
  if (!field) return null;
  const values = formValues[item.instanceItemId] ?? {};
  return { field, value: isFieldVisible(field, values, item.sections) ? values[key] : undefined };
}

/** The patient's own name/DOB: the slot's item first, then the package's other
 *  items; per mapping, the first visible nonblank answer wins. */
function patientIdentity(slotItem: V2Item, items: V2Item[], formValues: FormValues) {
  const ordered = [slotItem, ...items.filter((i) => i !== slotItem && i.kind !== 'agreement')];
  const value: Partial<Record<PatientMapping, unknown>> = {};
  const source: Partial<Record<PatientMapping, FieldRef>> = {};
  const all: FieldRef[] = [];
  PATIENT_MAPPINGS.forEach((mapping) => {
    ordered.forEach((item) => {
      const values = formValues[item.instanceItemId] ?? {};
      itemFields(item).forEach((field) => {
        if (field.ehr_mapping !== mapping) return;
        const ref = { itemId: item.instanceItemId, key: field.key };
        all.push(ref);
        if (source[mapping]) return;
        const v = isFieldVisible(field, values, item.sections) ? values[field.key] : undefined;
        if (typeof v === 'string' && v.trim() !== '') {
          value[mapping] = v;
          source[mapping] = ref;
        }
      });
    });
  });
  return { value, source, all };
}

export function planSlotCheck(slot: InsuranceSlot, items: V2Item[], formValues: FormValues): SlotCheckPlan {
  const keys = slotFieldKeys(slot.slot);
  const item = slot.item;
  const itemId = item.instanceItemId;
  const values = formValues[itemId] ?? {};
  const fieldRef = (key: string): FieldRef | null => (slotAnswer(item, key, formValues) ? { itemId, key } : null);

  const companyVisible = isFieldVisible(slot.companyField, values, item.sections);
  const companyRaw = companyVisible ? values[slot.companyField.key] : undefined;
  const companyName = typeof companyRaw === 'string' ? companyRaw.trim() : '';
  // The picked payer counts only alongside a company name (the payload's rule too).
  const payerId = companyName ? values[slot.companyField.payer_id_key] : undefined;

  const policyholder = slotAnswer(item, keys.policyholder, formValues)?.value;
  const choice = policyholderChoice(policyholder);
  const identity = patientIdentity(item, items, formValues);
  const request = buildInsuranceCheckRequest({
    slot: slot.slot,
    policyholder,
    payerId,
    memberId: slotAnswer(item, keys.memberId, formValues)?.value,
    insuredName: slotAnswer(item, keys.insuredName, formValues)?.value,
    insuredDob: slotAnswer(item, keys.insuredDob, formValues)?.value,
    relationship: slotAnswer(item, keys.relationship, formValues)?.value,
    // Only a package with none of these fields leaves the identity to the backend:
    // one with them still needs their answers (a hidden or blank one sends nothing).
    patient: identity.all.length === 0 ? null : {
      firstName: identity.value.first_name,
      lastName: identity.value.last_name,
      dob: identity.value.date_of_birth,
    },
  });

  const slotInputs = [keys.policyholder, keys.company, keys.memberId, keys.insuredName, keys.insuredDob, keys.relationship]
    .map(fieldRef)
    .filter((r): r is FieldRef => r !== null);
  const nameAndDobFields = choice === 'self'
    ? [identity.source.first_name, identity.source.last_name, identity.source.date_of_birth].filter((r): r is FieldRef => !!r)
    : [fieldRef(keys.insuredName), fieldRef(keys.insuredDob)].filter((r): r is FieldRef => r !== null);

  return {
    slot: slot.slot,
    request,
    fingerprint: request ? requestFingerprint(request) : null,
    policyholder: choice,
    companyName,
    inputs: choice === 'self' ? [...slotInputs, ...identity.all] : slotInputs,
    memberIdField: fieldRef(keys.memberId),
    nameAndDobFields,
  };
}

/** Whether a screen (one item's fields) holds any of a slot's check inputs. */
export function screenTouchesSlot(plan: SlotCheckPlan, screen: { itemId: string; fieldKeys: string[] }): boolean {
  return plan.inputs.some((r) => r.itemId === screen.itemId && screen.fieldKeys.indexOf(r.key) !== -1);
}

// ---------------------------------------------------------------------------
// Attempts and outcomes
// ---------------------------------------------------------------------------

/** Per-slot bookkeeping for one page session. */
export interface SlotLedger {
  attempts: number;
  lastFingerprint: string | null;
  lastPolicyFingerprint: string | null;
  /** null while the last request is in flight (or never answered). */
  lastOutcome: InsuranceCheckOutcome | 'error' | null;
  lastUnavailableReason: InsuranceUnavailableReason | null;
  /** The last non-null `check_id`, for the submission (#46 AC7, follow-up)... */
  checkId: string | null;
  /** ...and the fingerprint of the answers that check was for. */
  checkFingerprint: string | null;
}

/** A package instance's ledger. The page holds it, so it outlives a renderer remount. */
export type InsuranceLedger = Record<PolicySlot, SlotLedger>;

export function emptySlotLedger(): SlotLedger {
  return { attempts: 0, lastFingerprint: null, lastPolicyFingerprint: null, lastOutcome: null, lastUnavailableReason: null, checkId: null, checkFingerprint: null };
}

export function emptyInsuranceLedger(): InsuranceLedger {
  return { primary: emptySlotLedger(), secondary: emptySlotLedger() };
}

export type FixableOutcome = 'check_member_id' | 'check_name_or_dob' | 'not_found' | 'inactive';

/** Outcomes the patient can fix by correcting what they typed (decision 3: `inactive` too). */
export function isFixableOutcome(outcome: string | null): outcome is FixableOutcome {
  return outcome === 'check_member_id' || outcome === 'check_name_or_dob'
    || outcome === 'not_found' || outcome === 'inactive';
}

/**
 * Next sends the first check, changed answers after a fixable result, or corrected
 * payer/member identifiers after a provider refusal or failed check. An
 * explicit retry alone may repeat unchanged answers after a temporary failure.
 * Pending checks and the two-attempt cap always prevent another request.
 */
export function canSendCheck(ledger: SlotLedger, request: FormInsuranceCheckRequest, trigger: 'next' | 'retry' = 'next'): boolean {
  if (ledger.attempts >= MAX_CHECKS_PER_SLOT) return false;
  const fingerprint = requestFingerprint(request);
  if (trigger === 'retry') {
    return ledger.attempts === 1 && ledger.lastFingerprint === fingerprint
      && isTemporaryFailure(ledger);
  }
  if (ledger.lastFingerprint === fingerprint) return false;
  if (ledger.attempts === 0) return true;
  return isFixableOutcome(ledger.lastOutcome)
    || (ledger.attempts === 1 && ledger.lastOutcome === 'unavailable'
      && (ledger.lastUnavailableReason === 'provider_refused' || ledger.lastUnavailableReason === 'check_failed')
      && ledger.lastPolicyFingerprint !== null && ledger.lastPolicyFingerprint !== policyFingerprint(request));
}

export function recordCheckSent(ledger: SlotLedger, request: FormInsuranceCheckRequest): void {
  ledger.attempts += 1;
  ledger.lastFingerprint = requestFingerprint(request);
  ledger.lastPolicyFingerprint = policyFingerprint(request);
  ledger.lastOutcome = null;
  ledger.lastUnavailableReason = null;
}

export function recordCheckResult(
  ledger: SlotLedger,
  fingerprint: string,
  outcome: InsuranceCheckOutcome | 'error',
  checkId: string | null,
  reason: InsuranceUnavailableReason | null = null,
): void {
  ledger.lastOutcome = outcome;
  ledger.lastUnavailableReason = outcome === 'unavailable' || outcome === 'error' ? reason : null;
  if (checkId) {
    ledger.checkId = checkId;
    ledger.checkFingerprint = fingerprint;
  }
}

export function isTemporaryFailure(ledger: SlotLedger): boolean {
  return (ledger.lastOutcome === 'unavailable' || ledger.lastOutcome === 'error')
    && ledger.lastUnavailableReason === 'payer_unavailable';
}

/** Fixed advisory copy; never contains answers or response/error text. */
export function unavailableNotice(ledger: SlotLedger): { text: string; retry: boolean } | null {
  if (isTemporaryFailure(ledger)) {
    return ledger.attempts < MAX_CHECKS_PER_SLOT
      ? { text: "We couldn't reach your insurance company just now.", retry: true }
      : { text: "We still couldn't reach your insurance company. You can continue, and the office will check your coverage.", retry: false };
  }
  if (ledger.lastOutcome !== 'unavailable') return null;
  if (ledger.lastUnavailableReason === 'provider_refused') {
    return { text: "Your insurance company couldn't check this policy online for our office. You can continue, and the office will check your coverage.", retry: false };
  }
  if (ledger.lastUnavailableReason === 'check_failed') {
    return { text: "We couldn't check this policy online. You can continue, and the office will check your coverage.", retry: false };
  }
  return null;
}

/**
 * The `check_id` a submission may carry for a slot (#48): only while the slot's
 * current answers are the ones that check was for. After an edit there is none
 * and the backend runs its own check, so an `active` for old answers never
 * travels with new ones.
 */
export function submittableCheckId(ledger: SlotLedger, currentFingerprint: string | null): string | null {
  if (!currentFingerprint || !ledger.checkId) return null;
  return ledger.checkFingerprint === currentFingerprint ? ledger.checkId : null;
}

/** Every slot's submittable check_id for these answers: the submission's
 *  `insurance_checks`, before the payload builder shapes it. */
export function submittableInsuranceChecks(
  slots: InsuranceSlot[],
  items: V2Item[],
  formValues: FormValues,
  ledger: InsuranceLedger,
): Partial<Record<PolicySlot, string>> {
  const out: Partial<Record<PolicySlot, string>> = {};
  slots.forEach((slot) => {
    const checkId = submittableCheckId(ledger[slot.slot], planSlotCheck(slot, items, formValues).fingerprint);
    if (checkId) out[slot.slot] = checkId;
  });
  return out;
}

const FIXABLE_MESSAGES: Record<FixableOutcome, string> = {
  check_member_id: "Your insurance company couldn't find this member ID. Please check it.",
  check_name_or_dob: "These don't match your insurance company's records. Please check them.",
  not_found: "We couldn't find this policy. Please check the details.",
  inactive: "Your insurance company says this coverage isn't active.",
};

/** Where the fields an outcome is about sit, seen from the screen that shows it;
 *  'none' when the form has no such field. */
export type FieldPlacement = 'here' | 'earlier' | 'later' | 'none';

/**
 * The copy for a fixable outcome. On the fields' own screen it sits under the
 * highlighted field; from another screen it names what to check and where.
 * With no field to name (a "Me" check that took the name and DOB from the
 * patient record), it asks nothing of the patient.
 */
export function fixableMessage(outcome: FixableOutcome, placement: FieldPlacement, policyholder: PolicyholderChoice): string {
  if (placement === 'here' || outcome === 'not_found' || outcome === 'inactive') return FIXABLE_MESSAGES[outcome];
  if (outcome === 'check_member_id') {
    if (placement === 'none') return FIXABLE_MESSAGES[outcome];
    return `Your insurance company couldn't find the member ID you entered. Please check it on the ${placement} screen.`;
  }
  if (placement === 'none') {
    return 'Your insurance company has a different name or date of birth for you on this policy. You can continue, and the office will check it with you.';
  }
  const whose = policyholder === 'self' ? 'Your name or date of birth' : "The policyholder's name or date of birth";
  return `${whose} doesn't match your insurance company's records. Please check them on the ${placement} screen.`;
}

export function coverageConfirmedText(companyName: string): string {
  return companyName ? `Coverage confirmed with ${companyName}.` : 'Coverage confirmed.';
}
